import { useEffect, useMemo, useRef, useState } from "react";
import { states } from "@miplatform/constants";
import {
  getCityFromAddressComponents,
  getCountryFromAddressComponents,
  getCountyFromAddressComponents,
  getStateFromAddressComponents,
  isCityInAddress,
} from "@miplatform/utils";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useMaps } from "../useMaps/useMaps";

/**
 * @typedef {Object} UseMapFormProps
 * @property {Object} values - The current form values.
 * @property {Function} update - Function to update form values.
 * @property {Function} setValue - Function to update form values.
 * @property {Function} setERror - Function to set errors.
 * @property {Function} clearErrors - Function to clear errors.
 * @property {Function} watch - Function to watch values.
 * @property {Object} cities - Object containing cities indexed by state.
 * @property {Function} addCity - Function to add a new city to the state.
 * @property {Function} getCities - Function to fetch the list of cities.
 * @property {string} [stateFieldName="regionCode"] - Field name for the state.
 * @property {string} [cityFieldName="city"] - Field name for the city.
 * @property {string} [countyFieldName="county"] - Field name for the county.
 * @property {string} [longitudeFieldName="longitude"] - Field name for longitude.
 * @property {string} [latitudeFieldName="latitude"] - Field name for latitude.
 */

/**
 * @typedef {Object} UseMapFormReturn
 * @property {Object} map - Reference to the map object.
 * @property {string} city - Current city value.
 * @property {string} state - Current state value.
 * @property {number} lat - Current latitude value.
 * @property {number} lng - Current longitude value.
 * @property {boolean} isCityValid - Indicates whether the city is valid.
 * @property {Array<{ label: string, value: string }>} citiesOptions - List of city options for the current state.
 * @property {Function} handleGetCities - Function to fetch cities based on a query.
 * @property {Function} handleCityBlur - Function to handle city input blur events.
 * @property {Function} handleCenterOnLocation - Function to center the map on a specified location.
 * @property {Function} handleCityTextChange - Function to manage changes in the city input.
 * @property {Function} handleCoordChange - Function to handle updates to latitude and longitude coordinates.
 */

const FIELD_NAMES = {
  CITY: "city",
  COUNTY: "county",
  REGION_CODE: "regionCode",
  LONGITUDE: "longitude",
  LATITUDE: "latitude",
};

/**
 * Custom hook to manage map-related form state and interactions.
 *
 * This hook provides functionality for managing the form values related to a map,
 * including city, state, latitude, and longitude. It also offers functions to fetch
 * cities, validate the city, and update coordinates based on user interactions with the map.
 *
 * @param {UseMapFormProps} props - The properties for configuring the hook.
 * @returns {UseMapFormReturn} The values and functions for managing map form state.
 */
export const useMapForm = ({
  values,
  update,
  setValue,
  cities,
  addCity,
  getCities,
  setError,
  clearErrors,
  watch,
  isEditing = false,
  stateFieldName = FIELD_NAMES.REGION_CODE,
  cityFieldName = FIELD_NAMES.CITY,
  countyFieldName = FIELD_NAMES.COUNTY,
  longitudeFieldName = FIELD_NAMES.LONGITUDE,
  latitudeFieldName = FIELD_NAMES.LATITUDE,
} = {}) => {
  const isEditingRef = useRef(isEditing);
  const watchField = watch || ((fieldName) => values[fieldName]);
  const updateForm =
    update ||
    ((values) => {
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined) {
          setValue(key, values[key]);
        }
      });
    });
  const isMapClickedRef = useRef(false);
  const prevStateRef = useRef("");

  const map = useMap();
  const geocoding = useMapsLibrary("geocoding");
  const { getLatLng, searchCities } = useMaps();
  const city = watchField(cityFieldName);
  const state = watchField(stateFieldName);
  const lat = watchField(latitudeFieldName);
  const lng = watchField(longitudeFieldName);
  const prevValuesRef = useRef({
    [cityFieldName]: city,
    [stateFieldName]: state,
  });
  const [isCityValid, setIsCityValid] = useState(false);
  const citiesOptions = useMemo(
    () => cities[state]?.map((c) => ({ label: c, value: c })) || [],
    [cities, state]
  );

  /**
   * Checks if the current city exists in the list of cities for the current state.
   *
   * @returns {boolean} True if the city is valid, otherwise false.
   */
  const isCityInLocalState = () =>
    cities[state]?.some((c) => c.toLowerCase() === city?.toLowerCase());

  /**
   * Validates the current city by checking its existence in the current state's cities.
   */
  const handleCityValidation = () => {
    const isValid = isCityInLocalState();
    setIsCityValid(isValid);
    if (!isValid && setError) {
      setError(cityFieldName, {
        message: "City is not valid",
      });
    } else if (clearErrors) {
      clearErrors(cityFieldName);
    }
  };

  /**
   * Fetches cities based on a search query and adds them if they do not already exist.
   *
   * @param {string} query - The search query for cities.
   * @returns {Promise<void>} A promise that resolves when the cities are fetched and processed.
   */
  const handleGetCities = async (query) => {
    const results = await searchCities(query, state);
    results.forEach((c) => {
      if (!cities[state]?.includes(c)) {
        addCity(c, state);
      }
    });
  };

  /**
   * Handles the blur event for the city input.
   * Clears the city and county if the city is not valid.
   */
  const handleCityBlur = () => {
    if (!isCityValid) {
      updateForm({ [cityFieldName]: "", [countyFieldName]: "" });
    }
  };

  /**
   * Handles changes in the city input and triggers city fetching.
   *
   * @param {string} value - The current input value for the city.
   */
  const handleCityTextChange = (value) => {
    if (value) {
      handleGetCities(value);
    }
  };

  /**
   * Centers the map on a specified location based on state and city.
   *
   * @param {string} state - The state to center on.
   * @param {string} city - The city to center on.
   * @returns {Promise<void>} A promise that resolves when the map is centered.
   */
  const handleCenterOnLocation = async (state, city) => {
    if (!states[state]) {
      return;
    }

    const location = await getLatLng({ state: states[state], city });

    if (location) {
      map?.panTo(location);
      if (city) {
        handleCoordChange(location, { isClicked: false, city });
      }
    }
  };

  /**
   * Handles changes to coordinates and updates the corresponding state fields.
   *
   * @param {Object} coordinates - The latitude and longitude coordinates.
   * @param {number} coordinates.lat - The latitude value.
   * @param {number} coordinates.lng - The longitude value.
   * @param {Object} options - Options for handling the coordinate change.
   * @param {boolean} [options.isClicked=true] - Indicates if the change was due to a click.
   * @param {string|null} [options.city=null] - The current city (if applicable).
   */
  const handleCoordChange = (
    { lat, lng },
    { isClicked = true, city = null } = {}
  ) => {
    isMapClickedRef.current = isClicked;
    const geocoder = new geocoding.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (res, status) => {
      if (status === geocoding.GeocoderStatus.OK && res.length !== 0) {
        const address = res.find((a) => isCityInAddress(a, city));
        if (address) {
          const updates = {};
          const { address_components: components } = address;
          if (getCountryFromAddressComponents(components) === "US") {
            map?.panTo({ lat, lng });
            updates[latitudeFieldName] = Number(lat.toFixed(5));
            updates[longitudeFieldName] = Number(lng.toFixed(5));
          }
          const updatedCity = getCityFromAddressComponents(components);
          const updatedState = getStateFromAddressComponents(components);
          const updatedCounty = getCountyFromAddressComponents(components);
          if (updatedState && Object.keys(states)?.includes(updatedState)) {
            if (updatedCity && !cities[updatedState]?.includes(updatedCity)) {
              addCity(updatedCity, updatedState, updatedCounty);
            }
            updates[countyFieldName] = updatedCounty ?? "";
            updates[cityFieldName] = updatedCity ?? "";
            updates[stateFieldName] = updatedState;
          }
          updateForm(updates);
        }
      }
    });
  };

  useEffect(() => {
    getCities();
  }, []);

  useEffect(() => {
    if (
      state &&
      prevStateRef.current &&
      state !== prevStateRef.current &&
      !isMapClickedRef.current
    ) {
      updateForm({ [cityFieldName]: "", [countyFieldName]: "" });
    }
    prevStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (city && cities[state]) {
      handleCityValidation();
    }
  }, [city]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    if (isEditingRef.current !== isEditing) {
      isEditingRef.current = isEditing;
      return;
    }

    if (
      prevValuesRef.current[cityFieldName] !== undefined &&
      prevValuesRef.current[stateFieldName] !== undefined
    ) {
      if (!city && !isMapClickedRef.current) {
        handleCenterOnLocation(state);
      } else if (isCityInLocalState() && !isMapClickedRef.current) {
        handleCenterOnLocation(state, city);
      }
    }
    prevValuesRef.current = { [stateFieldName]: state, [cityFieldName]: city };

    isMapClickedRef.current = false;
  }, [state, city, cities]);

  return {
    map,
    city,
    state,
    lat,
    lng,
    isCityValid,
    cities,
    citiesOptions,
    handleGetCities,
    handleCityBlur,
    handleCenterOnLocation,
    handleCityTextChange,
    handleCoordChange,
  };
};
